import { h, Component, cloneElement } from 'preact';
import dlv from 'dlv';
import breakpointStrings from '@cube/ui/refs/variables.json';
import Provider from 'preact-context-provider';

// convert `foo: "100"` to `foo: 100`
const BREAKPOINTS = Object.keys(breakpointStrings).reduce((bps, key) => {
	bps[key] = parseInt(breakpointStrings[key],0);
	return bps;
}, {});

/**
 * Constants representing the different device values that can be applied
 * to a component wrapped using DeviceVariance.
 */
export const DEVICE_TYPE_PHONE='phone',
	DEVICE_TYPE_PHONE_PORTRAIT='phonePortrait',
	DEVICE_TYPE_PHONE_LANDSCAPE='phoneLandscape',
	DEVICE_TYPE_TABLET='tablet',
	DEVICE_TYPE_DESKTOP='desktop',
	DEVICE_TYPE_DESKTOP_LARGE='desktopLarge',
	DEVICE_TYPE_GROUP_MOBILE = [DEVICE_TYPE_PHONE, DEVICE_TYPE_TABLET],
	DEVICE_TYPE_GROUP_DESKTOP = [DEVICE_TYPE_DESKTOP, DEVICE_TYPE_DESKTOP_LARGE];


/**
 * Returns true if the supplied deviceType matches any of the supplied potential matches.
 * @param {String} deviceType A string representing a known device type.
 * @param {String|Array} potentialMatches A string representing a known device type, or any array of device type strings.
 */
export function isDeviceType(deviceType, potentialMatches) {
	if (!potentialMatches) return false;
	if (typeof potentialMatches === 'string') {
		return deviceType === potentialMatches;
	}
	return Array.isArray(potentialMatches) && potentialMatches.indexOf(deviceType) !== -1;
}


const BREAKPOINT_BOOTSTRAP = 'bootstrap';

/** Render matching props/children based on active device type.
 *
 * 	@example
 *	<DeviceVariance
 *		phone={<div>phone</div>}
 *		tablet={<div>tablet</div>}
 *		desktop={<div>desktop</div>}
 *		desktopLarge={<div>desktop (large)</div>}
 *	/>
 *
 *	@example
 *	<DeviceVariance desktop={false}>
 *		<div>renders on phone or tablet</div>
 *	</DeviceVariance>
 *
 *	@example
 *	<DeviceVariance phone={false} tablet={false}>
 *		<div>renders on desktop or large desktop</div>
 *	</DeviceVariance>
 *
 *	@example
 *	<DeviceVariance desktop={(
 *		<div>renders on desktop</div>
 *	)} />
 *
 *
 * Bootstrap mode:
 *	@example
 *	<DeviceVariance
 *		breakpointSet="bootstrap"
 *		phonePortrait={<div>phone portrait</div>}
 *		phoneLandscape={<div>phone landscape</div>}
 *		tablet={<div>tablet</div>}
 *		desktop={<div>desktop</div>}
 *		desktopLarge={<div>desktop (large)</div>}
 *	/>
 */
export default class DeviceVariance extends Component {

	handleResize = () => {
		this.setState({});
	};

	// determine viewport width, defaulting to 1000px if size is unavailable
	getViewportWidth() {
		return typeof window!=='undefined' && window.innerWidth || 1000;
	}

	// if we are in a Portal context, the derived device type is available at window.iMDS.Device
	getGen5Device() {
		return typeof window!=='undefined' && dlv(window, 'iMDS.Device');
	}

	/** Get rendered child via boostrap breakpoints
	 * @param {Number} viewportWidth The width of the screen
	 * @param {Function} desktopLarge The render function called when the largest breakpoint is hit
	 * @param {Function} desktop The render function called when the desktop breakpoint is hit
	 * @param {Function} tablet The render function called when the tablet breakpoint is hit
	 * @param {Function} phoneLandscape The render function called when the phone landscape is hit
	 * @param {Function} phonePortrait The render function called when phonePortrait, or zero
	 */
	getBootstrapChild(viewportWidth, { desktopLarge, desktop, tablet, phoneLandscape, phonePortrait, phone, children, ...restProps }) {
		let firstChild = children && children[0];

		let child;

		if (viewportWidth < BREAKPOINTS['screen-bootstrap-sm-px']) {
			child = phonePortrait || phone;
		}
		else if (viewportWidth < BREAKPOINTS['screen-bootstrap-md-px']) {
			child = phoneLandscape || phonePortrait || phone;
		}
		else if (viewportWidth < BREAKPOINTS['screen-bootstrap-lg-px']) {
			child = tablet || phoneLandscape || phonePortrait || phone;
		}
		else if (viewportWidth < BREAKPOINTS['screen-bootstrap-xl-px']) {
			child = desktop || tablet || phoneLandscape || phonePortrait || phone;
		}
		else {
			child = desktopLarge || desktop || tablet || phoneLandscape || phonePortrait || phone;
		}

		// if a prop is just a boolean, ignore it.
		child = child!==false && child!==true && (child || firstChild) || null;
		return child && child.nodeName ? cloneElement(child, { ...restProps }) : child;
	}

	/** Get default child via boostrap breakpoints
	 * @param {Number} viewportWidth The width of the screen
	 * @param {Function} desktopLarge The render function called when the largest breakpoint is hit
	 * @param {Function} desktop The render function called when the desktop breakpoint is hit
	 * @param {Function} tablet The render function called when the tablet breakpoint is hit
	 * @param {Function} phone The render function called when the phone landscape is hit
	 * @param {Function} mobile Alias of phone
	 * @param {Object} other
	 * @param {Function} fallback The fallback render function if needed
	 */
	getDefaultChild(viewportWidth, { desktopLarge, desktop, tablet, phone, mobile=phone, other, fallback, adaptive = false, children, ...restProps }) {
		let firstChild = children && children[0];

		let gen5Device = this.getGen5Device(),
			child;

		// if we are 'adaptive' instead of 'responsive', attempt to use gen5's opinion of what the device is
		if (adaptive && gen5Device){
			switch (gen5Device) {
				case DEVICE_TYPE_PHONE:
					child = phone;
					break;
				case DEVICE_TYPE_TABLET:
					child = tablet;
					break;
				case DEVICE_TYPE_DESKTOP:
					child = viewportWidth >= BREAKPOINTS['screen-lg-px'] && (desktopLarge || desktopLarge===false) ? desktopLarge : desktop;
					break;
			}
		}
		else if (viewportWidth < BREAKPOINTS['screen-sm-px']) {
			child = phone;
		}
		else if (viewportWidth < BREAKPOINTS['screen-md-px']) {
			child = tablet;
		}
		else if (viewportWidth < BREAKPOINTS['screen-lg-px']) {
			child = desktop;
		}
		else {
			child = viewportWidth >= BREAKPOINTS['screen-lg-px'] && (desktopLarge || desktopLarge===false) ? desktopLarge : desktop;
		}

		// if a prop is just a boolean, ignore it.
		child = child!==false && child!==true && (child || other || firstChild) || null;
		return child && child.nodeName ? cloneElement(child, { ...restProps }) : child;
	}

	// on initial mount, attempt to render for the given deviceType if sizing wasn't available during initial render
	componentDidMount() {
		addEventListener('resize', this.handleResize);
	}

	componentWillUnmount() {
		removeEventListener('resize', this.handleResize);
	}

	render({ breakpointSet, ...restProps }) {
		let viewportWidth = this.getViewportWidth();

		return (breakpointSet === BREAKPOINT_BOOTSTRAP) ?
			this.getBootstrapChild(viewportWidth, restProps) :
			this.getDefaultChild(viewportWidth, restProps);
	}
}

function upgradeV1Args({ phone, tablet, desktop, desktopLarge, ...restProps } = {}) {
	return restProps.variants && restProps || { ...restProps, variants: { phone, tablet, desktop, desktopLarge } };
}

/**
 * Wraps any component introducing a new prop called 'device'.
 * device will be automatically populated by a @cube/blocks DeviceVariance with
 * one of the values specified in the DeviceTypes const available in this package.
 *
 * If your component is supplied a device prop manually, then a DeviceVariance will not be used,
 * and the appropriate Variant will be placed directly.
 *
 * Exposes a `getWrappedComponent` function resulting wrapped Component so you can get the unwrapped component for unit testing purposes
 *
 * @param {Object} [variants] You may supply variants with keys matching the key names defined
 * in the DeviceTypes object to place different Components based on the value of device.
 * @returns {Function}
 */
export function deviceVariance(args) {
	return function (Child) {
		function DeviceVarianceWrapper(props, context) {
			return applyDeviceVariation(Child, props, context, upgradeV1Args(args));
		}

		DeviceVarianceWrapper.getWrappedComponent = Child && Child.getWrappedComponent || (() => Child);
		return DeviceVarianceWrapper;
	};
}

function variantForDevice(device, variants, fallback, props, useProvider = false) {
	//null or false are valid values for a variant. It specifies that we do not want anything to render rather than falling back.
	let desiredVariantExists = variants && variants[device] !== undefined;
	let variant = h(desiredVariantExists && variants[device] || fallback, { ...props, device } );
	return useProvider ? <Provider device={device} >{ variant }</Provider> : variant;
}

function applyDeviceVariation(Component, { device, ...restProps }, { device: deviceInContext }, { variants, ...restDeviceVarianceArgs } = {}) {

	//No component supplied:
	if (!Component) return;

	//device is supplied from props:
	if (device) {
		return variantForDevice(device, variants, Component, restProps, !deviceInContext);
	}

	//device is supplied from context:
	if (deviceInContext) {
		return variantForDevice(deviceInContext, variants, Component, restProps);
	}

	//Use DeviceVariance to determine device:
	return (
		<DeviceVariance
			phone={variantForDevice(DEVICE_TYPE_PHONE, variants, Component, restProps, !deviceInContext)}
			phonePortrait={variantForDevice(DEVICE_TYPE_PHONE_PORTRAIT, variants, Component, restProps, !deviceInContext)}
			phoneLandscape={variantForDevice(DEVICE_TYPE_PHONE_LANDSCAPE, variants, Component, restProps, !deviceInContext)}
			tablet={variantForDevice(DEVICE_TYPE_TABLET, variants, Component, restProps, !deviceInContext)}
			desktop={variantForDevice(DEVICE_TYPE_DESKTOP, variants, Component, restProps, !deviceInContext)}
			desktopLarge={variantForDevice(DEVICE_TYPE_DESKTOP_LARGE, variants, Component, restProps, !deviceInContext)}
			adaptive
			{...restDeviceVarianceArgs}
		/>
	);
}
